import { asText } from '@prismicio/client';
import type { CSSProperties } from 'react';

import {
  PromoCardSlice,
  PromoCarouselCtaType,
  PromoCarouselDocumentCtaType,
  PromoCarouselDocumentData,
  PromoCarouselDocumentDataCarouselItemsItem,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
} from '@/types';
import { getContentAlign, getCSSpropety } from '@/utils';

export const promoCardSliceToCarouselItem = (
  slice: PromoCardSlice,
): PromoCarouselDocumentDataCarouselItemsItemWithID => {
  const { primary } = slice;

  return {
    pos_title_name: primary.pos_title_name,
    background_color: primary.background_color,
    picture: primary.picture,
    pincture_only: 'picture_only' in primary ? primary.picture_only : false,
    title: primary.title,
    title_color: primary.title_color,
    description: primary.description,
    description_color: primary.description_color,
    cta_type: primary.cta_type,
    cta_background: primary.cta_background,
    cta_text: primary.cta_text,
    cta_text_color: primary.cta_text_color,
    cta_link: primary.cta_link,
    definedInPrismic: true,
    promoCardVariation: slice.variation,
    text_align: primary.text_align,
    card_inner_padding:
      'card_inner_padding' in primary ? primary.card_inner_padding : null,
  } as PromoCarouselDocumentDataCarouselItemsItemWithID;
};

const isPromoCardSlice = (
  slice: PromoCarouselDocumentData['slices'][number],
): slice is PromoCardSlice => slice.slice_type === 'promo_card';

export const getPromoCarouselItems = (
  promoCarousel?: PromoCarouselDocumentData | null,
): PromoCarouselDocumentDataCarouselItemsItem[] => {
  const promoCardSlices = promoCarousel?.slices?.filter(isPromoCardSlice) ?? [];
  const activeSlices = promoCardSlices?.filter(
    slice => slice?.primary?.active !== false,
  );

  if (activeSlices?.length) {
    return activeSlices.map(promoCardSliceToCarouselItem);
  }

  return promoCarousel?.carousel_items ?? [];
};

const getPromoCtaStyles = (
  ctaType: PromoCarouselDocumentCtaType | null | undefined,
  ctaBackground: string | null | undefined,
  ctaTextColor: string | null | undefined,
): {
  backgroundColor?: string;
  border?: string;
  ctaOutlander: boolean;
} => {
  if (ctaType === PromoCarouselCtaType.ButtonOutline) {
    return {
      border: `1px solid ${ctaTextColor ?? ''}`,
      ctaOutlander: true,
    };
  }

  if (ctaType === PromoCarouselCtaType.Link) {
    return { ctaOutlander: false };
  }

  if (ctaType === PromoCarouselCtaType.ButtonSolid) {
    if (ctaBackground) {
      return {
        backgroundColor: ctaBackground,
        ctaOutlander: false,
      };
    }

    return { ctaOutlander: false };
  }

  // Legacy fallback (Treez / items without cta_type): bg empty → outline
  if (ctaBackground) {
    return {
      backgroundColor: ctaBackground,
      ctaOutlander: false,
    };
  }

  return {
    border: `1px solid ${ctaTextColor ?? ''}`,
    ctaOutlander: true,
  };
};

export type PromoSpecialPresentation = {
  altText: string;
  /** Prismic card root / overlay content strip */
  cardBackgroundStyle: CSSProperties;
  cardAriaLabel: string;
  ctaBorder: CSSProperties;
  ctaMergeStyle: CSSProperties;
  ctaOutlander: boolean;
  ctaText: string | null | undefined;
  descriptionAriaLabel: string;
  descriptionStyle: CSSProperties;
  descriptionText: string;
  displayImageOnly: boolean;
  /** Prismic CMS card */
  definedInPrismic: boolean;
  hasImageUrl: boolean;
  pictureOnly: boolean;
  showCta: boolean;
  /** Prismic uses `<button>`; Treez overlay uses `<div>` */
  showCtaAsButton: boolean;
  titleAriaLabel: string;
  titleStyle: CSSProperties;
  titleText: string;
  treezContentBackground: CSSProperties;
};

export const getPromoSpecialPresentation = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
): PromoSpecialPresentation => {
  const {
    background_color,
    cta_background,
    cta_text,
    cta_text_color,
    description,
    description_color,
    title,
    title_color,
  } = special;

  const definedInPrismic = Boolean(special.definedInPrismic);

  const titleText = definedInPrismic
    ? title ?? ''
    : (special.areTreezTitleAndDescriptionPresent
        ? special.treezDiscountTitle
        : title) ?? '';

  const descriptionText = definedInPrismic
    ? asText(description) ?? ''
    : (special.areTreezTitleAndDescriptionPresent
        ? special.treezDiscountDescription
        : asText(description)) ?? '';

  const altText = special.pincture_only && !titleText ? 'Promotion image' : '';

  const cardAriaLabel = `Promotion card: ${titleText || 'Special offer'}`;
  const titleAriaLabel = `Promotion title: ${titleText}`;
  const descriptionAriaLabel = `Promotion description: ${descriptionText}`;

  const backgroundColor = getCSSpropety(
    'backgroundColor',
    background_color as string | null,
  );
  const titleStyle = getCSSpropety(
    'color',
    title_color as string | null,
  ) as CSSProperties;
  const descriptionStyle = getCSSpropety(
    'color',
    description_color as string | null,
  ) as CSSProperties;
  const ctaTextColor = getCSSpropety(
    'color',
    cta_text_color as string | null,
  ) as CSSProperties;

  const ctaStyles = getPromoCtaStyles(
    special.cta_type,
    cta_background as string | null,
    cta_text_color as string | null,
  );

  const ctaBackgroundColor = getCSSpropety(
    'backgroundColor',
    ctaStyles.backgroundColor ?? null,
  ) as CSSProperties;
  const ctaBorder = getCSSpropety('border', ctaStyles.border) as CSSProperties;

  const pictureOnly = Boolean(special.pincture_only);
  const displayImageOnly = Boolean(special.displayImageOnly);
  const hasImageUrl = Boolean(special.imageUrl);

  const showCtaAsButton = definedInPrismic;
  const showCta = Boolean(cta_text);

  const ctaOutlander = ctaStyles.ctaOutlander;

  const ctaMergeStyle: CSSProperties = {
    ...ctaBackgroundColor,
    ...ctaTextColor,
    ...ctaBorder,
  };

  const treezContentBackground: CSSProperties = hasImageUrl
    ? {}
    : { ...(backgroundColor as CSSProperties) };

  return {
    altText,
    cardBackgroundStyle: backgroundColor as CSSProperties,
    cardAriaLabel,
    ctaBorder,
    ctaMergeStyle,
    ctaOutlander,
    ctaText: cta_text,
    descriptionAriaLabel,
    descriptionStyle,
    descriptionText,
    displayImageOnly,
    definedInPrismic,
    hasImageUrl,
    pictureOnly,
    showCta,
    showCtaAsButton,
    titleAriaLabel,
    titleStyle,
    titleText,
    treezContentBackground,
  };
};

export type PromoCardMediaState = {
  cardBackgroundStyle: CSSProperties | undefined;
  showTreezImage: boolean;
  showPrismicImage: boolean;
  contentFullWidth: boolean;
};

export type PromoCardImageSource = 'treez' | 'prismic' | 'none';

/**
 * Resolves hero image, background, and content width for the default PromoCard variation.
 *
 * Custom POS (`definedInPrismic: true`): Prismic picture when present; root background when
 * `background_color` is set (including when a picture exists — legacy split layout).
 *
 * Default path (`definedInPrismic: false`): legacy split when CMS `background_color`
 * and text content; else Treez `imageUrl` full-bleed, or bg-only full card.
 */
export const getPromoCardMediaState = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
  pres: PromoSpecialPresentation,
): PromoCardMediaState => {
  if (pres.pictureOnly || pres.displayImageOnly) {
    return {
      cardBackgroundStyle: undefined,
      showTreezImage: pres.hasImageUrl,
      showPrismicImage: Boolean(special.picture?.url),
      contentFullWidth: false,
    };
  }

  if (special.definedInPrismic) {
    const showBackground = Boolean(special.background_color);

    return {
      cardBackgroundStyle: showBackground
        ? pres.cardBackgroundStyle
        : undefined,
      showTreezImage: false,
      showPrismicImage: Boolean(special.picture?.url),
      contentFullWidth: false,
    };
  }

  if (special.background_color && !pres.pictureOnly && !pres.displayImageOnly) {
    return {
      cardBackgroundStyle: pres.cardBackgroundStyle,
      showTreezImage: pres.hasImageUrl,
      showPrismicImage: !pres.hasImageUrl && Boolean(special.picture?.url),
      contentFullWidth: false,
    };
  }

  const showTreezImage = pres.hasImageUrl;
  const showDefaultBackground =
    !showTreezImage && Boolean(special.background_color);
  const showPrismicFallbackImage =
    !showTreezImage && !showDefaultBackground && Boolean(special.picture?.url);

  return {
    cardBackgroundStyle: showDefaultBackground
      ? pres.cardBackgroundStyle
      : undefined,
    showTreezImage,
    showPrismicImage: showPrismicFallbackImage,
    contentFullWidth: showDefaultBackground,
  };
};

/**
 * Resolves which image source to render inside a `<figure>`.
 * Does not apply bg-only suppression (used by Split and as a building block).
 *
 * Custom POS: Prismic picture when `picture.url` exists.
 * Default path: Treez image, else Prismic fallback — `background_color` is ignored.
 */
export const getPromoCardImageSource = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
  pres: PromoSpecialPresentation,
): PromoCardImageSource => {
  if (special.definedInPrismic) {
    return special.picture?.url ? 'prismic' : 'none';
  }

  if (pres.hasImageUrl) {
    return 'treez';
  }

  return special.picture?.url ? 'prismic' : 'none';
};

/** Inline styles for text block alignment from the Prismic `text_align` field. */
export const getPromoContentAlignStyle = (
  special: PromoCarouselDocumentDataCarouselItemsItemWithID,
): CSSProperties =>
  special.text_align
    ? {
        alignItems: getContentAlign(special.text_align),
        textAlign: special.text_align,
      }
    : {};
